// src/templateManager/FieldEditor.jsx
// Component for editing individual context field definitions

import { Card, Group, TextInput, Select, Textarea, ActionIcon, Stack, MultiSelect, Switch } from '@mantine/core';
import { IconTrash, IconArrowUp, IconArrowDown, IconGripVertical } from '@tabler/icons-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'dropdown', label: 'Dropdown (Single Select)' },
  { value: 'multiselect', label: 'Multi-Select' }
];

// Convert camelCase to Title Case for display
function toTitleCase(str) {
  if (!str) return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (['EU', 'ROC', 'AUC', 'URL', 'PII'].includes(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

// Convert Title Case back to camelCase for storage
function toCamelCase(str) {
  if (!str) return '';
  return str
    .trim()
    .split(/\s+/)
    .map((word, i) => i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

export default function FieldEditor({ field, onUpdate, onDelete, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const handleChange = (key, value) => {
    onUpdate({ ...field, [key]: value });
  };

  return (
    <Card withBorder p="md" sx={{ background: '#f9fafb' }}>
      <Stack spacing="sm">
        {/* Header with drag handle and actions */}
        <Group position="apart">
          <Group>
            <IconGripVertical size={16} style={{ cursor: 'grab', color: '#adb5bd' }} />
            <TextInput
              placeholder="Field name (e.g., Use Case Description)"
              value={toTitleCase(field.name || '')}
              onChange={(e) => handleChange('name', toCamelCase(e.target.value))}
              styles={{ input: { fontWeight: 500 } }}
              style={{ flex: 1, minWidth: 250 }}
            />
          </Group>

          <Group spacing="xs">
            <ActionIcon onClick={onMoveUp} disabled={!canMoveUp} variant="subtle">
              <IconArrowUp size={16} />
            </ActionIcon>
            <ActionIcon onClick={onMoveDown} disabled={!canMoveDown} variant="subtle">
              <IconArrowDown size={16} />
            </ActionIcon>
            <ActionIcon onClick={onDelete} color="red" variant="subtle">
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Field configuration */}
        <Group grow>
          <Select
            label="Field Type"
            data={FIELD_TYPES}
            value={field.type || 'text'}
            onChange={(value) => handleChange('type', value)}
          />

          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
            <Switch
              label="Required field"
              checked={field.required || false}
              onChange={(e) => handleChange('required', e.target.checked)}
            />
          </div>
        </Group>

        <Textarea
          label="Description / Help Text"
          placeholder="Describe what this field is for..."
          value={field.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          autosize
          minRows={2}
        />

        {/* Options for dropdown/multiselect */}
        {(field.type === 'dropdown' || field.type === 'multiselect') && (
          <MultiSelect
            label="Options"
            placeholder="Add options..."
            data={field.options || []}
            value={field.options || []}
            onChange={(value) => handleChange('options', value)}
            creatable
            searchable
            getCreateLabel={(query) => `+ Add "${query}"`}
            onCreate={(query) => {
              const newOptions = [...(field.options || []), query];
              handleChange('options', newOptions);
              return query;
            }}
          />
        )}

        {/* Default value */}
        {field.type === 'text' && (
          <TextInput
            label="Default Value (optional)"
            placeholder="Default text..."
            value={field.defaultValue || ''}
            onChange={(e) => handleChange('defaultValue', e.target.value)}
          />
        )}
      </Stack>
    </Card>
  );
}
